import {
  Alert,
  Col,
  Form,
  FormInstance,
  Input,
  Radio,
  Row,
  Statistic,
  Tooltip,
} from 'antd';
import React, { useEffect } from 'react';
import { FloatingFormItem, InputDecimal } from '@/components/_common';
import { useDefaultEmptyFieldError } from '@/hooks';
import Authorities from '@/types/Authority';
import { useWorkorderDetailContext } from '@/components/Movements/WorkOrders/Details/WorkorderDetailProvider';
import { useWorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import AreasInfiniteSelectField from '@/components/_common/AreasInfiniteSelectField';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import AntdFeatherIcon from '@/components/_common/AntdFeatherIcon';
import { HelpCircle } from 'react-feather';
import { FormattedMessage } from 'react-intl';
import SupplierSelect from '@/components/Suppliers/SupplierSelect';
import { quantityFormatter } from '@/utils/number';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { ComplementReplicateItemFormData } from '..';
import formatterPrice from '@/utils/number/formatterPrice';

interface Props {
  form: FormInstance<ComplementReplicateItemFormData>;
}

const defaultStatistcProps = {
  decimalSeparator: ',',
  groupSeparator: '.',
};

const ReplicateComplementUpdateItemForm: React.FC<Props> = ({ form }) => {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const quantity = Form.useWatch(['quantity'], form);
  const unitPrice = Form.useWatch(['unitPrice'], form);
  const discountValue = Form.useWatch(['discountValue'], form);
  const isInternal = Form.useWatch(['isInternal'], form);

  const subTotal = (quantity || 0) * (unitPrice || 0);
  const amount = subTotal - discountValue;

  const hasDiscountAboveDefault = hasPermissions([
    Authorities.MOVIMENTATION_WORKORDER_ADD_DISCOUNT_PERCENTAGE_GREATER_THAN_DEFINED_BY_THE_COMPANY,
  ]);

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const {
    data: config,
    mutateAsync: getConfig,
    isLoading: configIsLoading,
  } = useWorkorderConfigurationCompany();

  useEffect(() => {
    if (workorder?.company) {
      getConfig(workorder.company.id);
    }
  }, [workorder]);

  useEffect(() => {
    if (config) {
      form.setFieldValue(
        'configDiscount',
        (config.maxServiceDiscount || 0) * 100,
      );
    }
  }, []);

  return (
    <Form form={form} layout="vertical">
      {!hasDiscountAboveDefault &&
        !config?.maxServiceDiscount &&
        !configIsLoading && (
          <Alert
            type="warning"
            description="Não há configuração de desconto de serviço a empresa relacionada."
            style={{ marginBottom: 24 }}
          />
        )}

      <Form.Item hidden name="configDiscount" initialValue={0}>
        <Input />
      </Form.Item>

      <Row gutter={[16, 8]}>
        <Col span={24} md={12} lg={10}>
          <FloatingFormItem
            label="Descrição"
            name="itemName"
            rules={[
              defaultEmptyFieldError,
              { type: 'string', max: 120, message: 'Máximo de 120 caracteres' },
            ]}
          >
            <Input
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_CHANGE_DESCRIPTION,
                ])
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            label="Área"
            name="subAreaId"
            rules={[defaultEmptyFieldError]}
          >
            <AreasInfiniteSelectField />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label="Tempo"
            name="quantity"
            rules={[defaultEmptyFieldError]}
            initialValue={1}
            getValueFromEvent={(value) => {
              const discount = form.getFieldValue('discount');
              const unitPrice = form.getFieldValue('unitPrice');

              const discountValue = (value * unitPrice * discount) / 100 || 0;

              form.setFieldsValue({ discountValue });

              return value;
            }}
          >
            <InputDecimal
              disabled={
                complement?.status !== WorkOrderComplementStatus.Open &&
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_CHANGE_QUANTITY,
                ])
              }
              min={0}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label="Preço Unit."
            name="unitPrice"
            initialValue={0}
            getValueFromEvent={(value) => {
              const discount = form.getFieldValue('discount');
              const quantity = form.getFieldValue('quantity');

              const discountValue = (value * quantity * discount) / 100 || 0;

              form.setFieldsValue({ discountValue });

              return value;
            }}
          >
            <InputDecimal
              disabled={
                (complement?.status !== WorkOrderComplementStatus.Open &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_OPENED_ITEM_CHANGE_VALUE,
                  ])) ||
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_SERVICE_EDIT_SALE_VALUE,
                ])
              }
              min={0}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label="Desconto (%)"
            name="discount"
            initialValue={0}
            getValueFromEvent={(value) => {
              const calc = ((subTotal || 0) * (value || 0)) / 100;

              form.setFieldValue('discountValue', calc);

              return value;
            }}
            rules={[
              () => ({
                max: !hasDiscountAboveDefault
                  ? (config?.maxServiceDiscount ?? 0) * 100
                  : undefined,
                type: 'number',
                message: `Desconto não pode ser maior que ${formatterPrice(
                  Number((config?.maxServiceDiscount ?? 0) * 100),
                )}`,
              }),
            ]}
          >
            <InputDecimal
              min={0}
              disabled={
                !unitPrice ||
                (!hasDiscountAboveDefault && !config?.maxServiceDiscount) ||
                (isInternal === 'true' &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_GIVE_DISCOUNT_OUTSOURCED_SERVICES,
                  ]))
              }
              key={config?.company.id}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label="Desconto (R$)"
            name="discountValue"
            initialValue={0}
            getValueFromEvent={(value) => {
              const calc = (100 * (value || 0)) / (subTotal || 0);

              form.setFieldValue('discount', calc);

              return value;
            }}
            rules={[
              ({ getFieldValue }) => ({
                max: !hasDiscountAboveDefault
                  ? getFieldValue('unitPrice') || 0
                  : undefined,
                type: 'number',
                message: 'Desconto não pode ser maior que o preço',
              }),
            ]}
          >
            <InputDecimal
              min={0}
              max={unitPrice || 0}
              disabled={
                !unitPrice ||
                (!hasDiscountAboveDefault && !config?.maxServiceDiscount) ||
                (isInternal === 'true' &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_GIVE_DISCOUNT_OUTSOURCED_SERVICES,
                  ]))
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6}>
          <FloatingFormItem
            label="Preço de custo (Unit.)"
            name="unitCost"
            initialValue={0}
            rules={[
              {
                type: 'number',
                required: !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_SAVE_WITHOUT_INFORMING_COST,
                ]),
                min: !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_SAVE_WITHOUT_INFORMING_COST,
                ])
                  ? 1
                  : 0,
              },
            ]}
          >
            <InputDecimal
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_EDIT_COST_PRICE,
                ]) ||
                (complement?.status !== WorkOrderComplementStatus.Open &&
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_OPENED_ITEM_CHANGE_COST_PRICE,
                  ]))
              }
              min={0}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={8}>
          <Form.Item
            name="isPaintedPart"
            initialValue="false"
            label={
              <span style={{ display: 'inline-flex' }}>
                Contar como peça pintada
                <Tooltip overlay="Comissão por peça pintada">
                  <AntdFeatherIcon
                    icon={<HelpCircle style={{ marginLeft: 5 }} />}
                    size={16}
                  />
                </Tooltip>
              </span>
            }
          >
            <Radio.Group>
              <Radio value="true">
                <FormattedMessage id="commons.options.yes" />
              </Radio>
              <Radio value="false">
                <FormattedMessage id="commons.options.no" />
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        <Col span={24} md={12} lg={6}>
          <Form.Item
            name="isInternal"
            initialValue="true"
            label="Serviço terceirizado?"
          >
            <Radio.Group>
              <Radio value="false">
                <FormattedMessage id="commons.options.yes" />
              </Radio>

              <Radio value="true">
                <FormattedMessage id="commons.options.no" />
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>

        {!JSON.parse(isInternal ?? false) && (
          <Col span={24} md={12} lg={10}>
            <FloatingFormItem
              name="supplierId"
              label="Fornecedor"
              rules={[
                {
                  required: !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_ITEM_SAVE_WITHOUT_INFORMING_SUPPLIER,
                  ]),
                },
              ]}
            >
              <SupplierSelect />
            </FloatingFormItem>
          </Col>
        )}

        <Col span={24}>
          <Statistic
            {...defaultStatistcProps}
            title={
              <FormattedMessage id="workOrders.details.items.serviceTotal" />
            }
            value={quantityFormatter(amount)}
            prefix="R$"
            valueStyle={{ color: '#364068' }}
            style={{ float: 'right' }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default ReplicateComplementUpdateItemForm;
